// SearchRecommendationContext.tsx

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

import { DEFAULT_RECOMMENDATION } from "../constants";
import { RecommendationType, WEBVIEW_MESSAGE_TYPE } from "../enums";
import { useRecommendations } from "../hooks/useRecommendation";
import { CartItem } from "../types/cart.types";
import {
  SearchRecommendationContextType,
} from "../types/model.types";
import { getQueryParams } from "../utlis/getQueryParams";
import { mapRecommendationProductsToProductCards } from "../utlis/helper.service";

const SearchRecommendationContext =
  createContext<SearchRecommendationContextType>({
    recommendations: DEFAULT_RECOMMENDATION,
    productCards: [],
    loading: false,
    type: RecommendationType.RECOMMENDED_FOR_YOU,
    cartItem: [],
  });

interface Props {
  children: ReactNode;
}

export const SearchRecommendationProvider: React.FC<Props> = ({ children }) => {
  const { type, storeId, jsonFilters, productId } = getQueryParams();
  const [cartItem, setCartItem] = useState<CartItem[]>([]);

  const recommendationParams = useMemo(
    () => ({ type, storeId, jsonFilters, productId }),
    [type, storeId, jsonFilters, productId]
  );

  const { recommendations, loading } =
    useRecommendations(recommendationParams);

  const productCards = useMemo(() => {
    return mapRecommendationProductsToProductCards(
      recommendations?.products || []
    );
  }, [recommendations]);

  const handleMessage = (event: MessageEvent) => {
    const { type, message } = event.data;
    try {
      switch (type) {
        case WEBVIEW_MESSAGE_TYPE.BASKET_ITEMS:
          setCartItem(message);
          break;
      }
    } catch {}
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const receiver = window;
    receiver.addEventListener("message", handleMessage as EventListener);
    return () => {
      receiver.removeEventListener("message", handleMessage as EventListener);
    };
  }, []);

  return (
    <SearchRecommendationContext.Provider
      value={{
        recommendations: recommendations ?? DEFAULT_RECOMMENDATION,
        productCards,
        loading,
        type,
        cartItem,
      }}
    >
      {children}
    </SearchRecommendationContext.Provider>
  );
};

export const useSearchRecommendations = () =>
  useContext(SearchRecommendationContext);